import React, { useState, useEffect, useCallback, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import {
  fetchSkierResortViewLive,
  fetchSeasonSkiResortReport,
  fetchSeasonSkiResortSkiers,
  fetchSeasonSkiResortResortDays
} from '../services/reportsService';
import { fetchSkiAreas } from '../services/skiAreasService';
import { ANALYTICS_TIMEZONE, wallTimeToInstant } from '../utils/timezone';
import { formatHours } from '../utils/formatHours';
import { skiDateToRecordedDay } from '../utils/recordedDayTrack';
import { useError } from '../contexts/ErrorContext';
import { MAPBOX_STYLE } from '../constants/mapbox';
import ReportChart from './ReportChart';
import AnalyticsTimezoneNote from './AnalyticsTimezoneNote';
import Modal from './Modal';
import './SkierResortViewReport.css';

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN;

// Live map layer IDs
const LIVE_SOURCE    = 'skier-resorts';
const LIVE_HEATMAP   = 'skier-resorts-heatmap';
const LIVE_CIRCLE    = 'skier-resorts-circle';
const LIVE_LABEL     = 'skier-resorts-label';

// Season map layer IDs
const SEASON_SOURCE  = 'season-resorts';
const SEASON_HEATMAP = 'season-resorts-heatmap';
const SEASON_CIRCLE  = 'season-resorts-circle';
const SEASON_LABEL   = 'season-resorts-label';

/**
 * Format an ISO timestamp as a short local time for the "last seen" column.
 */
const formatLastSeen = (isoString) => {
  if (!isoString) return '—';
  const d = new Date(isoString);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

/**
 * Date cell for a Season skier row: the day itself, or first – last for a
 * skier with several days (their row expands to list each one).
 */
const formatSkiDates = (daily = []) => {
  const dates = daily.map(d => d.ski_date).sort();
  if (dates.length === 0) return '—';
  return dates.length === 1 ? dates[0] : `${dates[0]} – ${dates[dates.length - 1]}`;
};

/**
 * Opens one skier's recorded day on the map. A link inside a clickable row, so
 * it stops the click from also opening the session.
 */
const DayMapLink = ({ uid, skiDate }) => {
  if (!uid || !skiDate) return null;
  return (
    <a
      href={`/users/${uid}/recorded-days/${skiDateToRecordedDay(skiDate)}/map`}
      target="_blank"
      rel="noopener noreferrer"
      className="skier-resort-view-map-link"
      onClick={e => e.stopPropagation()}
    >
      Map View
    </a>
  );
};

/**
 * Per-day breakdown under an expanded skier row. Each date links to that
 * skier's analytics for the day. Only Season has per-day logged app time and Map View.
 */
const SkierDaysTable = ({ skier, getUserDayHref, showAppHours = false, showMapLink = false }) => (
  <table className="skier-resort-view-nested-table skier-resort-view-day-table">
    <thead>
      <tr>
        <th>Ski date</th>
        {showAppHours && <th className="skier-resort-season-col-num">Logged App Time</th>}
        <th className="skier-resort-season-col-num">Goggles Connected Duration</th>
        {showMapLink && <th>Map</th>}
      </tr>
    </thead>
    <tbody>
      {skier.daily.map(d => {
        const dayHref = getUserDayHref(skier.user_id, d.ski_date);
        return (
          <tr key={d.ski_date}>
            <td>
              {dayHref ? (
                <a
                  href={dayHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="skier-resort-view-day-link"
                >
                  {d.ski_date}
                </a>
              ) : (
                d.ski_date
              )}
            </td>
            {showAppHours && <td className="skier-resort-season-col-num">{formatHours(d.hours)}</td>}
            <td className="skier-resort-season-col-num">{formatHours(d.connected_hours)}</td>
            {showMapLink && <td><DayMapLink uid={skier.user_id} skiDate={d.ski_date} /></td>}
          </tr>
        );
      })}
    </tbody>
  </table>
);

/**
 * Content of the "?" modal. The connection limits repeat MIN_SESSION_SEC and
 * MAX_SESSION_HOURS in backend/reports/goggles_usage/sessions.js.
 */
const ColumnMeanings = () => (
  <>
    <section className="skier-resort-view-info-section">
      <h4>Goggles Connected Duration</h4>
      <p>
        Time the goggles were connected to the phone that day, added up across every
        connection. A connection counts once it has ended and lasted between 90 seconds and
        20 hours, so one still in progress doesn&apos;t show yet.
      </p>
      <p>
        It always covers the whole day, even with a resort selected. Connected doesn&apos;t
        always mean skiing: a connection in the car or the lodge counts too.
      </p>
    </section>

    <section className="skier-resort-view-info-section">
      <h4>Logged App Time</h4>
      <p>
        Time from the first to the last event the app logged, in app sessions that reported
        being at a resort. Sessions with no resort report don&apos;t count. With a resort
        selected, only that resort&apos;s sessions count.
      </p>
    </section>

    <section className="skier-resort-view-info-section">
      <h4>Why the two differ</h4>
      <p>
        They measure different things, so either can be higher. <strong>Connected time is
        higher</strong> when the goggles stay connected while the app logs nothing, or when a
        connection falls outside a session that reported a resort.
      </p>
      <p>
        <strong>0m connected with Logged App Time</strong> means the app reported a resort but
        the goggles never connected that day, or no connection qualified.
      </p>
    </section>

    <section className="skier-resort-view-info-section">
      <h4>Other columns</h4>
      <dl className="skier-resort-view-info-terms">
        <dt>Skiers (Live)</dt>
        <dd>Skiers seen in the last 24 hours, each counted at the resort they reported most recently.</dd>
        <dt>Last seen</dt>
        <dd>Time of the skier&apos;s most recent resort report.</dd>
        <dt>Unique Skier Days</dt>
        <dd>Different skiers at the resort over the date range. A skier counts once however many days they skied.</dd>
        <dt>Ski Days</dt>
        <dd>One per skier, per resort, per day. A skier at two resorts on one day counts once at each.</dd>
        <dt>Days</dt>
        <dd>Days the skier reported being at a resort. Days are in the Pacific timezone.</dd>
        <dt>Date</dt>
        <dd>The ski day, or first – last when there are several. Expand the row to list each day.</dd>
      </dl>
    </section>
  </>
);

const today = new Date().toISOString().slice(0, 10);
// Season view defaults to the trailing two weeks ending today.
const defaultSeasonStart = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000)
  .toISOString()
  .slice(0, 10);

/**
 * Extract a [lon, lat] center from any GeoJSON geometry.
 * Handles Point, Polygon, and MultiPolygon.
 */
const geometryCenter = (geometry) => {
  if (!geometry) return null;
  if (geometry.type === 'Point') {
    return [geometry.coordinates[0], geometry.coordinates[1]];
  }
  if (geometry.type === 'Polygon') {
    const ring = geometry.coordinates[0];
    return [
      ring.reduce((s, c) => s + c[0], 0) / ring.length,
      ring.reduce((s, c) => s + c[1], 0) / ring.length
    ];
  }
  if (geometry.type === 'MultiPolygon') {
    const ring = geometry.coordinates[0][0];
    return [
      ring.reduce((s, c) => s + c[0], 0) / ring.length,
      ring.reduce((s, c) => s + c[1], 0) / ring.length
    ];
  }
  return null;
};

/**
 * Fit a Mapbox map to a set of [lon, lat] points.
 * @param {mapboxgl.Map} map
 * @param {Array<{lon: number, lat: number}>} geoRows
 */
const fitMapToRows = (map, geoRows) => {
  if (geoRows.length === 0) return;
  if (geoRows.length === 1) {
    map.flyTo({ center: [geoRows[0].lon, geoRows[0].lat], zoom: 7, duration: 1200 });
    return;
  }
  const bounds = geoRows.reduce(
    (b, row) => b.extend([row.lon, row.lat]),
    new mapboxgl.LngLatBounds([geoRows[0].lon, geoRows[0].lat], [geoRows[0].lon, geoRows[0].lat])
  );
  map.fitBounds(bounds, { padding: 100, maxZoom: 7, duration: 1200 });
};

/**
 * Add heatmap + circle + label layers to a Mapbox map for a given source and
 * value property (e.g. 'skiers' for live, 'ski_days' for season).
 */
const addResortLayers = (map, sourceId, heatmapId, circleId, labelId, valueProp, onCircleClick) => {
  map.addLayer({
    id: heatmapId,
    type: 'heatmap',
    source: sourceId,
    maxzoom: 8,
    paint: {
      'heatmap-weight': ['interpolate', ['linear'], ['get', valueProp], 0, 0, 100, 1],
      'heatmap-intensity': ['interpolate', ['linear'], ['zoom'], 0, 1, 8, 3],
      'heatmap-color': [
        'interpolate', ['linear'], ['heatmap-density'],
        0,   'rgba(0,0,0,0)',
        0.1, '#0ea5e9',
        0.3, '#22c55e',
        0.6, '#fbbf24',
        0.8, '#f97316',
        1,   '#ef4444'
      ],
      'heatmap-radius': ['interpolate', ['linear'], ['zoom'], 0, 24, 8, 48],
      'heatmap-opacity': 0.85
    }
  });

  map.addLayer({
    id: circleId,
    type: 'circle',
    source: sourceId,
    minzoom: 4,
    paint: {
      'circle-radius': ['interpolate', ['linear'], ['get', valueProp], 1, 8, 50, 28],
      'circle-color': [
        'interpolate', ['linear'], ['get', valueProp],
        1,  '#0ea5e9',
        10, '#22c55e',
        30, '#fbbf24',
        50, '#ef4444'
      ],
      'circle-opacity': 0.85,
      'circle-stroke-color': '#ffffff',
      'circle-stroke-width': 1.5
    }
  });

  map.addLayer({
    id: labelId,
    type: 'symbol',
    source: sourceId,
    minzoom: 4,
    layout: {
      'text-field': ['to-string', ['get', valueProp]],
      'text-font': ['DIN Offc Pro Bold', 'Arial Unicode MS Bold'],
      'text-size': 11
    },
    paint: {
      'text-color': '#ffffff',
      'text-halo-color': 'rgba(0,0,0,0.6)',
      'text-halo-width': 1
    }
  });

  map.on('click', circleId, onCircleClick);
  map.on('mouseenter', circleId, () => { map.getCanvas().style.cursor = 'pointer'; });
  map.on('mouseleave', circleId, () => { map.getCanvas().style.cursor = ''; });
};

/**
 * SkierResortViewReport
 *
 * Two tabs:
 *   Live   – rolling 24h heatmap + resort table
 *   Season – date-range picker → heatmap + per-resort unique skiers & ski days
 *
 * Each tab lists every skier under the map; selecting a resort row narrows
 * that list to the resort.
 */
const SkierResortViewReport = () => {
  const { addError } = useError();
  const [activeTab, setActiveTab] = useState('live');
  const [infoOpen, setInfoOpen] = useState(false);

  // ── Live tab state ──────────────────────────────────────────────────────────
  const liveMapContainer = useRef(null);
  const liveMapRef = useRef(null);
  const [liveLoading, setLiveLoading] = useState(true);
  const [tableData, setTableData] = useState([]);
  const [generatedAt, setGeneratedAt] = useState(null);

  // ── Live skier table (every skier; a resort row click narrows it) ──────────
  const [liveSkiers, setLiveSkiers] = useState([]);
  const [liveResortNames, setLiveResortNames] = useState({});
  const [expandedLiveResortId, setExpandedLiveResortId] = useState(null);
  // Which skier's per-day breakdown is open: a skier the 24h window caught on
  // both yesterday and today.
  const [expandedLiveSkierId, setExpandedLiveSkierId] = useState(null);

  // ── Season tab state ────────────────────────────────────────────────────────
  const seasonMapContainer = useRef(null);
  const seasonMapRef = useRef(null);
  const [seasonStart, setSeasonStart] = useState(defaultSeasonStart);
  const [seasonEnd, setSeasonEnd] = useState(today);
  const [seasonLoading, setSeasonLoading] = useState(false);
  const [seasonRows, setSeasonRows] = useState(null);
  const [seasonDailyBreakdown, setSeasonDailyBreakdown] = useState(null);
  const [seasonGeneratedAt, setSeasonGeneratedAt] = useState(null);
  const [seasonRangeLabel, setSeasonRangeLabel] = useState(null);
  const [seasonTimezone, setSeasonTimezone] = useState(null);

  // ── Season skier table, across every resort ────────────────────────────────
  const [seasonAllSkiers, setSeasonAllSkiers] = useState(null);
  const [seasonAllLoading, setSeasonAllLoading] = useState(false);
  const [seasonAllError, setSeasonAllError] = useState(null);
  const [seasonAllTotal, setSeasonAllTotal] = useState(null);
  const [seasonAllTruncated, setSeasonAllTruncated] = useState(false);
  const [seasonResortNames, setSeasonResortNames] = useState({});
  // Tags each fetch, so a slow response for an earlier range can't overwrite
  // a newer one. Go re-enables before this table has finished loading.
  const seasonAllRequestRef = useRef(0);

  // ── Season resort drilldown (row click → skier days/hours at that resort) ──
  const [expandedSeasonResortId, setExpandedSeasonResortId] = useState(null);
  const [seasonResortSkiers, setSeasonResortSkiers] = useState(null);
  const [seasonResortLoading, setSeasonResortLoading] = useState(false);
  const [seasonResortError, setSeasonResortError] = useState(null);
  const [seasonResortTotal, setSeasonResortTotal] = useState(null);
  const [seasonResortTruncated, setSeasonResortTruncated] = useState(false);
  // Which skier's per-day breakdown is open, scoped to the resort above.
  const [expandedSkierId, setExpandedSkierId] = useState(null);
  // The start/end that actually produced seasonRows — not the (possibly
  // edited-but-not-submitted) date inputs — so a resort drilldown always
  // matches the range on screen rather than whatever's mid-edit above it.
  const [seasonQueryRange, setSeasonQueryRange] = useState(null);

  // ── Live map init ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (activeTab !== 'live') return;
    if (!liveMapContainer.current || liveMapRef.current) return;

    mapboxgl.accessToken = MAPBOX_TOKEN;
    const map = new mapboxgl.Map({
      container: liveMapContainer.current,
      style: MAPBOX_STYLE,
      center: [10, 30],
      zoom: 1.5,
      pitch: 0
    });
    liveMapRef.current = map;

    return () => {
      map.remove();
      liveMapRef.current = null;
    };
  }, [activeTab]);

  // ── Season map init ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (activeTab !== 'season') return;
    if (!seasonMapContainer.current || seasonMapRef.current) return;

    mapboxgl.accessToken = MAPBOX_TOKEN;
    const map = new mapboxgl.Map({
      container: seasonMapContainer.current,
      style: MAPBOX_STYLE,
      center: [10, 30],
      zoom: 1.5,
      pitch: 0
    });
    seasonMapRef.current = map;

    return () => {
      map.remove();
      seasonMapRef.current = null;
    };
  }, [activeTab]);

  // ── Live map layers ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!liveMapRef.current || tableData.length === 0) return;

    const map = liveMapRef.current;
    const geojson = {
      type: 'FeatureCollection',
      features: tableData.map(row => ({
        type: 'Feature',
        properties: { skiers: row.skiers, name: row.name, resortId: row.resortId },
        geometry: { type: 'Point', coordinates: [row.lon, row.lat] }
      }))
    };

    const update = () => {
      if (map.getSource(LIVE_SOURCE)) {
        map.getSource(LIVE_SOURCE).setData(geojson);
      } else {
        map.addSource(LIVE_SOURCE, { type: 'geojson', data: geojson });
        addResortLayers(map, LIVE_SOURCE, LIVE_HEATMAP, LIVE_CIRCLE, LIVE_LABEL, 'skiers', (e) => {
          const { name, skiers } = e.features[0].properties;
          const coords = e.features[0].geometry.coordinates.slice();
          new mapboxgl.Popup({ closeButton: false, className: 'resort-popup' })
            .setLngLat(coords)
            .setHTML(`<span class="resort-popup-name">${name}</span><span class="resort-popup-count">${skiers} skier${skiers !== 1 ? 's' : ''}</span>`)
            .addTo(map);
        });
      }
      fitMapToRows(map, tableData.filter(r => Number.isFinite(r.lon) && Number.isFinite(r.lat)));
    };

    if (map.loaded()) update();
    else {
      map.once('load', update);
      return () => map.off('load', update);
    }
  }, [tableData]);

  // ── Season map layers ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!seasonMapRef.current || !seasonRows || seasonRows.length === 0) return;

    const map = seasonMapRef.current;
    const geoRows = seasonRows.filter(r => Number.isFinite(r.lon) && Number.isFinite(r.lat));
    if (geoRows.length === 0) return;

    const geojson = {
      type: 'FeatureCollection',
      features: geoRows.map(row => ({
        type: 'Feature',
        properties: {
          ski_days: row.ski_days,
          unique_skier_days: row.unique_skier_days,
          name: row.resort_name || row.resort_id
        },
        geometry: { type: 'Point', coordinates: [row.lon, row.lat] }
      }))
    };

    const update = () => {
      if (map.getSource(SEASON_SOURCE)) {
        map.getSource(SEASON_SOURCE).setData(geojson);
      } else {
        map.addSource(SEASON_SOURCE, { type: 'geojson', data: geojson });
        addResortLayers(map, SEASON_SOURCE, SEASON_HEATMAP, SEASON_CIRCLE, SEASON_LABEL, 'ski_days', (e) => {
          const { name, ski_days, unique_skier_days } = e.features[0].properties;
          const coords = e.features[0].geometry.coordinates.slice();
          new mapboxgl.Popup({ closeButton: false, className: 'resort-popup' })
            .setLngLat(coords)
            .setHTML(
              `<span class="resort-popup-name">${name}</span>` +
              `<span class="resort-popup-count">${ski_days.toLocaleString()} ski day${ski_days !== 1 ? 's' : ''}</span>` +
              `<span class="resort-popup-sub">${unique_skier_days.toLocaleString()} unique skier${unique_skier_days !== 1 ? 's' : ''}</span>`
            )
            .addTo(map);
        });
      }
      fitMapToRows(map, geoRows);
    };

    if (map.loaded()) update();
    else {
      map.once('load', update);
      return () => map.off('load', update);
    }
  }, [seasonRows]);

  // ── Live data fetch ─────────────────────────────────────────────────────────
  const fetchLiveData = useCallback(async () => {
    setLiveLoading(true);
    setExpandedLiveResortId(null);
    setExpandedLiveSkierId(null);
    try {
      const [liveData, skiAreas] = await Promise.all([
        fetchSkierResortViewLive(addError),
        fetchSkiAreas()
      ]);

      const resortLookup = {};
      skiAreas.forEach(area => {
        const center = geometryCenter(area.geometry);
        if (center && Number.isFinite(center[0]) && Number.isFinite(center[1])) {
          resortLookup[area.id] = { name: area.name, lon: center[0], lat: center[1] };
        } else {
          console.warn('[SkierResortView] Skipping ski area with invalid coordinates:', area.id, area.name);
        }
      });

      console.log('[SkierResortView] Raw API response — users:', liveData.users.length);
      console.log('[SkierResortView] Ski areas in lookup:', Object.keys(resortLookup).length);

      const resortCounts = {};
      liveData.users.forEach(({ resort_id }) => {
        if (!resort_id) return;
        resortCounts[resort_id] = (resortCounts[resort_id] || 0) + 1;
      });

      const rows = Object.entries(resortCounts)
        .filter(([id]) => {
          if (!resortLookup[id]) {
            console.warn('[SkierResortView] resort_id not found in ski areas:', id);
            return false;
          }
          return true;
        })
        .map(([id, count]) => ({
          resortId: id,
          name: resortLookup[id].name,
          lon: resortLookup[id].lon,
          lat: resortLookup[id].lat,
          skiers: count
        }))
        .filter(row => Number.isFinite(row.lon) && Number.isFinite(row.lat))
        .sort((a, b) => b.skiers - a.skiers);

      setTableData(rows);
      setLiveSkiers(liveData.users);
      setLiveResortNames(Object.fromEntries(skiAreas.map(area => [area.id, area.name])));
      setGeneratedAt(liveData.generatedAt);
    } catch {
      // errors already sent to ErrorContext via service
    } finally {
      setLiveLoading(false);
    }
  }, [addError]);

  useEffect(() => {
    fetchLiveData();
  }, [fetchLiveData]);

  // Clicking a resort row narrows the skier table to it. Clicking the row
  // that's already selected goes back to every skier.
  const handleLiveRowClick = useCallback((resortId) => {
    setExpandedLiveResortId(prev => (prev === resortId ? null : resortId));
  }, []);

  const handleLiveSkierRowClick = useCallback((skierKey) => {
    setExpandedLiveSkierId(prev => (prev === skierKey ? null : skierKey));
  }, []);

  // ── Season data fetch ───────────────────────────────────────────────────────
  const fetchSeasonData = useCallback(async () => {
    if (!seasonStart || !seasonEnd) return;
    if (new Date(seasonStart) > new Date(seasonEnd)) {
      addError('Start date must be before or equal to end date.', null, 'Season Ski Resort Report');
      return;
    }

    setSeasonLoading(true);
    setSeasonRows(null);
    setSeasonDailyBreakdown(null);
    setExpandedSeasonResortId(null);
    setSeasonResortSkiers(null);
    setSeasonResortError(null);
    setExpandedSkierId(null);
    setSeasonQueryRange(null);

    // Not awaited: this query scans every event in the range, so the map and
    // resort table shouldn't wait on it.
    const skiersRequest = ++seasonAllRequestRef.current;
    setSeasonAllSkiers(null);
    setSeasonAllError(null);
    setSeasonAllLoading(true);
    fetchSeasonSkiResortSkiers(seasonStart, seasonEnd, addError)
      .then((result) => {
        if (skiersRequest !== seasonAllRequestRef.current) return;
        setSeasonAllSkiers(result.skiers || []);
        setSeasonAllTotal(result.totalSkiers ?? null);
        setSeasonAllTruncated(!!result.truncated);
      })
      .catch((err) => {
        if (skiersRequest !== seasonAllRequestRef.current) return;
        setSeasonAllError(err.message || 'Failed to load skiers.');
      })
      .finally(() => {
        if (skiersRequest === seasonAllRequestRef.current) setSeasonAllLoading(false);
      });

    try {
      const [reportData, skiAreas] = await Promise.all([
        fetchSeasonSkiResortReport(seasonStart, seasonEnd, addError),
        fetchSkiAreas()
      ]);

      const resortLookup = {};
      skiAreas.forEach(area => {
        const center = geometryCenter(area.geometry);
        resortLookup[area.id] = {
          name: area.name,
          lon: center && Number.isFinite(center[0]) ? center[0] : null,
          lat: center && Number.isFinite(center[1]) ? center[1] : null
        };
      });

      const enriched = reportData.resorts.map(r => ({
        ...r,
        resort_name: resortLookup[r.resort_id]?.name || null,
        lon: resortLookup[r.resort_id]?.lon ?? null,
        lat: resortLookup[r.resort_id]?.lat ?? null
      }));

      setSeasonRows(enriched);
      setSeasonResortNames(Object.fromEntries(skiAreas.map(area => [area.id, area.name])));
      setSeasonDailyBreakdown(
        (reportData.dailyBreakdown || []).map(d => ({ date: d.date, value: d.ski_days }))
      );
      setSeasonGeneratedAt(reportData.generatedAt);
      setSeasonRangeLabel(`${reportData.dateRange.start} – ${reportData.dateRange.end}`);
      setSeasonTimezone(reportData.timezone || null);
      setSeasonQueryRange(reportData.dateRange);
    } catch {
      // errors already forwarded to ErrorContext
    } finally {
      setSeasonLoading(false);
    }
  }, [seasonStart, seasonEnd, addError]);

  const handleSeasonKeyDown = (e) => {
    if (e.key === 'Enter') fetchSeasonData();
  };

  // Clicking a resort row narrows the skier table to that resort's days/hours
  // for the current date range. Clicking the selected row goes back to every
  // skier.
  const handleSeasonRowClick = useCallback(async (resortId) => {
    if (resortId === expandedSeasonResortId) {
      setExpandedSeasonResortId(null);
      setSeasonResortSkiers(null);
      setSeasonResortError(null);
      setExpandedSkierId(null);
      return;
    }
    setExpandedSeasonResortId(resortId);
    setSeasonResortSkiers(null);
    setSeasonResortError(null);
    setExpandedSkierId(null);
    setSeasonResortLoading(true);
    // Use the range that actually produced the table on screen, not
    // whatever's currently sitting in the (possibly edited-but-unsubmitted)
    // date inputs — a drilldown must match the aggregate row it came from.
    const range = seasonQueryRange || { start: seasonStart, end: seasonEnd };
    try {
      const result = await fetchSeasonSkiResortResortDays(resortId, range.start, range.end, addError);
      setSeasonResortSkiers(result.skiers || []);
      setSeasonResortTotal(result.totalSkiers ?? null);
      setSeasonResortTruncated(!!result.truncated);
    } catch (err) {
      setSeasonResortError(err.message || 'Failed to load skiers for this resort.');
    } finally {
      setSeasonResortLoading(false);
    }
  }, [expandedSeasonResortId, seasonQueryRange, seasonStart, seasonEnd, addError]);

  // A skier row's own expand — only meaningful when the API sent a `daily`
  // breakdown, i.e. days > 1. Only one skier's breakdown open at a time.
  const handleSkierRowClick = useCallback((userKey) => {
    setExpandedSkierId(prev => (prev === userKey ? null : userKey));
  }, []);

  // Deep-link into a skier's own analytics, windowed to one specific ski
  // day — mirrors OtaAttemptsReport's getUserHref. Day boundaries are
  // resolved in ANALYTICS_TIMEZONE (the same zone the backend buckets
  // ski_date in) and sent as absolute instants (+tz) so the target page
  // shows the same day regardless of the viewer's own timezone.
  const getUserDayHref = useCallback((uid, dateStr) => {
    if (!uid || !dateStr) return null;
    const start = wallTimeToInstant(`${dateStr}T00:00`, ANALYTICS_TIMEZONE);
    const end = wallTimeToInstant(`${dateStr}T23:59`, ANALYTICS_TIMEZONE);
    if (!start || !end) return null;
    const params = new URLSearchParams({
      tab: 'analytics',
      startDate: start.toISOString(),
      endDate: end.toISOString(),
      tz: ANALYTICS_TIMEZONE
    });
    return `/users/${uid}?${params.toString()}`;
  }, []);

  const liveLastUpdated = generatedAt ? new Date(generatedAt).toLocaleTimeString() : '—';
  const seasonLastUpdated = seasonGeneratedAt ? new Date(seasonGeneratedAt).toLocaleString() : null;

  const visibleLiveSkiers = expandedLiveResortId
    ? liveSkiers.filter(skier => skier.resort_id === expandedLiveResortId)
    : liveSkiers;

  // The Season skier table shows one of two lists, chosen by the selected resort.
  const seasonSkiers = expandedSeasonResortId ? seasonResortSkiers : seasonAllSkiers;
  const seasonSkiersLoading = expandedSeasonResortId ? seasonResortLoading : seasonAllLoading;
  const seasonSkiersError = expandedSeasonResortId ? seasonResortError : seasonAllError;
  const seasonSkiersTotal = expandedSeasonResortId ? seasonResortTotal : seasonAllTotal;
  const seasonSkiersTruncated = expandedSeasonResortId ? seasonResortTruncated : seasonAllTruncated;

  return (
    <div className="skier-resort-view-report">
      <div className="skier-resort-view-header">
        <div className="skier-resort-view-header-left">
          <div className="skier-resort-view-title-section">
            <h1 className="skier-resort-view-title">
              Skier Resort View
              <button
                type="button"
                className="skier-resort-view-info-btn"
                aria-label="What the columns mean"
                title="What the columns mean"
                onClick={() => setInfoOpen(true)}
              >
                ?
              </button>
            </h1>
            <p className="skier-resort-view-description">
              {activeTab === 'live'
                ? 'Live 24h view of skiers by resort'
                : 'Per-resort unique skiers and ski days over a date range'}
            </p>
          </div>
        </div>
        {activeTab === 'live' && (
          <button
            className="skier-resort-view-refresh-btn"
            onClick={fetchLiveData}
            type="button"
            disabled={liveLoading}
          >
            {liveLoading ? 'Loading…' : 'Refresh'}
          </button>
        )}
      </div>

      <div className="skier-resort-view-content">
        <div className="skier-resort-view-tabs">
          <button
            type="button"
            className={`skier-resort-view-tab${activeTab === 'live' ? ' active' : ''}`}
            onClick={() => setActiveTab('live')}
          >
            Live
          </button>
          <button
            type="button"
            className={`skier-resort-view-tab${activeTab === 'season' ? ' active' : ''}`}
            onClick={() => setActiveTab('season')}
          >
            Season
          </button>
        </div>

        {/* ── Live tab ─────────────────────────────────────────────────────── */}
        {activeTab === 'live' && (
          <div className="skier-resort-view-live-wrap">
            <div className="skier-resort-view-live">
              <div className="skier-resort-view-map-container" ref={liveMapContainer} />
              <div className="skier-resort-view-table-container">
                <div className="skier-resort-view-table-header">
                  <h3 className="skier-resort-view-table-title">Resort summary</h3>
                  <span className="skier-resort-view-table-updated">Updated {liveLastUpdated}</span>
                </div>
                <div className="skier-resort-view-table-wrapper">
                  {liveLoading ? (
                    <div className="skier-resort-view-loading">Loading…</div>
                  ) : tableData.length === 0 ? (
                    <div className="skier-resort-view-empty">No skiers online today</div>
                  ) : (
                    <table className="skier-resort-view-table">
                      <thead>
                        <tr>
                          <th>Resort</th>
                          <th>Skiers</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tableData.map(row => {
                          const isSelected = row.resortId === expandedLiveResortId;
                          return (
                            <tr
                              key={row.resortId}
                              className={`skier-resort-view-row-clickable${isSelected ? ' selected' : ''}`}
                              onClick={() => handleLiveRowClick(row.resortId)}
                            >
                              <td>
                                <span className={`skier-resort-view-chevron${isSelected ? ' open' : ''}`}>▸</span>
                                {row.name}
                              </td>
                              <td>{row.skiers}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>

            <div className="skier-resort-view-detail-panel">
              <div className="skier-resort-view-detail-header">
                <h3 className="skier-resort-view-detail-title">
                  {expandedLiveResortId
                    ? `Skiers at ${tableData.find(r => r.resortId === expandedLiveResortId)?.name || expandedLiveResortId}`
                    : `All skiers${liveLoading ? '' : ` (${liveSkiers.length.toLocaleString()})`}`}
                </h3>
                {expandedLiveResortId && (
                  <button
                    type="button"
                    className="skier-resort-view-detail-close"
                    onClick={() => handleLiveRowClick(expandedLiveResortId)}
                    aria-label="Show all skiers"
                  >
                    ×
                  </button>
                )}
              </div>
              <div className="skier-resort-view-detail-body">
                {liveLoading ? (
                  <div className="skier-resort-view-drill-status">Loading…</div>
                ) : visibleLiveSkiers.length > 0 ? (
                  <table className="skier-resort-view-detail-table">
                    <thead>
                      <tr>
                        <th>Skier</th>
                        <th>Email</th>
                        {!expandedLiveResortId && <th>Resort</th>}
                        <th>Last seen</th>
                        <th className="skier-resort-season-col-num">Goggles Connected Duration</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleLiveSkiers.map((skier, i) => {
                        const skierKey = skier.user_pseudo_id || String(i);
                        const expandable = skier.daily?.length > 1;
                        const skierOpen = expandable && expandedLiveSkierId === skierKey;
                        return (
                          <React.Fragment key={skierKey}>
                            <tr
                              className={`skier-resort-view-skier-row${expandable ? ' expandable' : ''}`}
                              onClick={expandable ? () => handleLiveSkierRowClick(skierKey) : null}
                            >
                              <td>
                                <span className={`skier-resort-view-chevron small${expandable ? '' : ' invisible'}${skierOpen ? ' open' : ''}`}>▸</span>
                                {skier.user_name || (
                                  <span className="skier-resort-view-unresolved">{skier.user_pseudo_id}</span>
                                )}
                              </td>
                              <td className="skier-resort-view-email-cell">{skier.user_email || '—'}</td>
                              {!expandedLiveResortId && (
                                <td className="skier-resort-view-resort-cell">
                                  {liveResortNames[skier.resort_id] || skier.resort_id}
                                </td>
                              )}
                              <td>{formatLastSeen(skier.last_seen)}</td>
                              <td className="skier-resort-season-col-num">{formatHours(skier.connected_hours)}</td>
                            </tr>
                            {skierOpen && (
                              <tr className="skier-resort-view-drill-row">
                                <td colSpan={expandedLiveResortId ? 4 : 5}>
                                  <SkierDaysTable skier={skier} getUserDayHref={getUserDayHref} />
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <div className="skier-resort-view-drill-status">No skiers found.</div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Season tab ───────────────────────────────────────────────────── */}
        {activeTab === 'season' && (
          <div className="skier-resort-view-season">
            <div className="skier-resort-season-controls">
              <div className="skier-resort-season-control-group">
                <label className="skier-resort-season-label" htmlFor="season-start-date">
                  Start Date
                </label>
                <input
                  id="season-start-date"
                  className="skier-resort-season-date-input"
                  type="date"
                  value={seasonStart}
                  onChange={e => setSeasonStart(e.target.value)}
                  onKeyDown={handleSeasonKeyDown}
                />
              </div>
              <div className="skier-resort-season-control-group">
                <label className="skier-resort-season-label" htmlFor="season-end-date">
                  End Date
                </label>
                <input
                  id="season-end-date"
                  className="skier-resort-season-date-input"
                  type="date"
                  value={seasonEnd}
                  onChange={e => setSeasonEnd(e.target.value)}
                  onKeyDown={handleSeasonKeyDown}
                />
              </div>
              <button
                className="skier-resort-season-go-btn"
                onClick={fetchSeasonData}
                type="button"
                disabled={seasonLoading || !seasonStart || !seasonEnd}
              >
                {seasonLoading ? 'Loading…' : 'Go'}
              </button>
            </div>

            <AnalyticsTimezoneNote timezone={seasonTimezone} />

            <div className="skier-resort-season-body">
              <div
                className="skier-resort-view-map-container skier-resort-season-map"
                ref={seasonMapContainer}
              />

              <div className="skier-resort-view-table-container">
                <div className="skier-resort-view-table-header">
                  <h3 className="skier-resort-view-table-title">Resort summary</h3>
                  {seasonLastUpdated && (
                    <span className="skier-resort-view-table-updated">
                      {seasonRangeLabel}
                    </span>
                  )}
                </div>
                <div className="skier-resort-view-table-wrapper">
                  {seasonLoading && (
                    <div className="skier-resort-view-loading">Loading…</div>
                  )}
                  {!seasonLoading && seasonRows === null && (
                    <div className="skier-resort-view-empty">
                      Select a date range and click Go.
                    </div>
                  )}
                  {!seasonLoading && seasonRows !== null && seasonRows.length === 0 && (
                    <div className="skier-resort-view-empty">
                      No data found for {seasonRangeLabel}.
                    </div>
                  )}
                  {!seasonLoading && seasonRows !== null && seasonRows.length > 0 && (
                    <table className="skier-resort-view-table">
                      <thead>
                        <tr>
                          <th>Resort</th>
                          <th className="skier-resort-season-col-num">Unique Skier Days</th>
                          <th className="skier-resort-season-col-num">Ski Days</th>
                        </tr>
                      </thead>
                      <tbody>
                        {seasonRows.map(row => {
                          const isSelected = row.resort_id === expandedSeasonResortId;
                          return (
                            <tr
                              key={row.resort_id}
                              className={`skier-resort-view-row-clickable${isSelected ? ' selected' : ''}`}
                              onClick={() => handleSeasonRowClick(row.resort_id)}
                            >
                              <td>
                                <span className={`skier-resort-view-chevron${isSelected ? ' open' : ''}`}>▸</span>
                                {row.resort_name || (
                                  <span className="skier-resort-season-unknown">{row.resort_id}</span>
                                )}
                              </td>
                              <td className="skier-resort-season-col-num">
                                {row.unique_skier_days.toLocaleString()}
                              </td>
                              <td className="skier-resort-season-col-num">
                                {row.ski_days.toLocaleString()}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>

            {(seasonSkiersLoading || seasonSkiersError || seasonSkiers) && (
              <div className="skier-resort-view-detail-panel skier-resort-season-skiers">
                <div className="skier-resort-view-detail-header">
                  <h3 className="skier-resort-view-detail-title">
                    {expandedSeasonResortId
                      ? `Skiers at ${seasonRows?.find(r => r.resort_id === expandedSeasonResortId)?.resort_name || expandedSeasonResortId}`
                      : `All skiers${seasonAllTotal != null && !seasonAllLoading ? ` (${seasonAllTotal.toLocaleString()})` : ''}`}
                  </h3>
                  {expandedSeasonResortId && (
                    <button
                      type="button"
                      className="skier-resort-view-detail-close"
                      onClick={() => handleSeasonRowClick(expandedSeasonResortId)}
                      aria-label="Show all skiers"
                    >
                      ×
                    </button>
                  )}
                </div>
                <div className="skier-resort-view-detail-body">
                  {seasonSkiersLoading ? (
                    <div className="skier-resort-view-drill-status">Loading…</div>
                  ) : seasonSkiersError ? (
                    <div className="skier-resort-view-drill-status error">{seasonSkiersError}</div>
                  ) : seasonSkiers && seasonSkiers.length > 0 ? (
                    <>
                      <table className="skier-resort-view-detail-table">
                        <thead>
                          <tr>
                            <th>Skier</th>
                            <th>Email</th>
                            {!expandedSeasonResortId && <th>Resorts</th>}
                            <th>Date</th>
                            <th className="skier-resort-season-col-num">Days</th>
                            <th className="skier-resort-season-col-num">Logged App Time</th>
                            <th className="skier-resort-season-col-num">Goggles Connected Duration</th>
                            <th>Map</th>
                          </tr>
                        </thead>
                        <tbody>
                          {seasonSkiers.map((skier, i) => {
                            const skierKey = skier.user_pseudo_id || String(i);
                            const expandable = skier.days > 1;
                            const skierOpen = expandable && expandedSkierId === skierKey;
                            // A 1-day skier has nothing to expand into — clicking
                            // the row itself goes straight to their analytics for
                            // that one day, same destination a multi-day skier
                            // reaches by expanding then clicking the date.
                            const singleDayHref = !expandable
                              ? getUserDayHref(skier.user_id, skier.daily?.[0]?.ski_date)
                              : null;
                            const rowClassName = expandable
                              ? 'skier-resort-view-skier-row expandable'
                              : singleDayHref
                                ? 'skier-resort-view-skier-row linkable'
                                : 'skier-resort-view-skier-row';
                            const rowOnClick = expandable
                              ? () => handleSkierRowClick(skierKey)
                              : singleDayHref
                                ? () => window.open(singleDayHref, '_blank', 'noopener,noreferrer')
                                : null;
                            return (
                              <React.Fragment key={skierKey}>
                                <tr className={rowClassName} onClick={rowOnClick}>
                                  <td>
                                    <span className={`skier-resort-view-chevron small${expandable ? '' : ' invisible'}${skierOpen ? ' open' : ''}`}>▸</span>
                                    {skier.user_name || (
                                      <span className="skier-resort-view-unresolved">{skier.user_pseudo_id}</span>
                                    )}
                                  </td>
                                  <td className="skier-resort-view-email-cell">{skier.user_email || '—'}</td>
                                  {!expandedSeasonResortId && (
                                    <td className="skier-resort-view-resort-cell">
                                      {skier.resort_ids.map(id => seasonResortNames[id] || id).join(', ')}
                                    </td>
                                  )}
                                  <td className="skier-resort-season-date-cell">{formatSkiDates(skier.daily)}</td>
                                  <td className="skier-resort-season-col-num">{skier.days}</td>
                                  <td className="skier-resort-season-col-num">{formatHours(skier.hours)}</td>
                                  <td className="skier-resort-season-col-num">{formatHours(skier.connected_hours)}</td>
                                  <td>{!expandable && <DayMapLink uid={skier.user_id} skiDate={skier.daily?.[0]?.ski_date} />}</td>
                                </tr>
                                {skierOpen && (
                                  <tr className="skier-resort-view-drill-row">
                                    <td colSpan={expandedSeasonResortId ? 7 : 8}>
                                      <SkierDaysTable skier={skier} getUserDayHref={getUserDayHref} showAppHours showMapLink />
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                      {seasonSkiersTruncated && (
                        <div className="skier-resort-view-drill-note">
                          Showing top {seasonSkiers.length} of {seasonSkiersTotal?.toLocaleString()} skiers.
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="skier-resort-view-drill-status">No skiers found.</div>
                  )}
                </div>
              </div>
            )}

            {seasonDailyBreakdown && seasonDailyBreakdown.length > 0 && (
              <div className="skier-resort-season-chart-container">
                <ReportChart
                  id="season-ski-days-daily"
                  name="Ski Days per Day"
                  type="timeseries"
                  data={seasonDailyBreakdown}
                />
              </div>
            )}
          </div>
        )}
      </div>

      <Modal
        isOpen={infoOpen}
        onClose={() => setInfoOpen(false)}
        title="What the columns mean"
        subtitle="Skier Resort View"
        className="skier-resort-view-info-modal"
      >
        <ColumnMeanings />
      </Modal>
    </div>
  );
};

export default SkierResortViewReport;
